import { QuestRoutine, QuestSeries } from "@joonapp/web-shared"
import { useEffect, useMemo } from "react"

import { useAllQuestsSearchStore } from "./useAllQuestsSearchStore"
import useQuestBoardDndFilters from "./useQuestBoardDndFilters"
import { useQuestDndSectionsStore } from "./useQuestDndSectionsStore"
import { sortQuestSeriesSection } from "../../../../util/util"
import useQuestBoardStore from "../../useQuestBoardStore"

export const useQuestDndSections = () => {
  const { activeQuests } = useQuestBoardDndFilters()
  const { selectedUserId, selectedFilterType } = useQuestBoardStore()
  const { sections, setSections, setUserHasMadeChanges } =
    useQuestDndSectionsStore()
  const { searchQuery } = useAllQuestsSearchStore()

  const { routineSections } = useMemo(
    () => createQuestRoutineSections(activeQuests),
    [activeQuests]
  )

  const sectionsLengths = useMemo(
    () => routineSections.map((section) => section[1].length).join(","),
    [routineSections]
  )

  useEffect(() => {
    const isNotSetYet = sections.every((section) => section[1].length === 0)
    if (activeQuests.length && isNotSetYet) setSections(routineSections)
  }, [routineSections]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSections(routineSections)
  }, [selectedUserId, selectedFilterType, sectionsLengths]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchQuery.length > 0) {
      setSections(routineSections)
    }
  }, [searchQuery]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setUserHasMadeChanges(!isEqual(sections, routineSections))
  }, [sections]) // eslint-disable-line react-hooks/exhaustive-deps
}

const createQuestRoutineSections = (questList: QuestSeries[]) => {
  const routineKeys = [
    QuestRoutine.MORNING,
    QuestRoutine.AFTERNOON,
    QuestRoutine.NIGHT,
    QuestRoutine.ANYTIME,
  ]

  const routineSections: Record<string, QuestSeries[]> = Object.fromEntries(
    routineKeys.map((key) => [key, []])
  )

  questList?.forEach((quest) => {
    const routineKey = quest.routine
    if (routineKey in routineSections) {
      routineSections[routineKey].push(quest)
    }
  })

  const sortedRoutineSections = sortQuestSections(routineSections).map(
    ([key, quests]) =>
      [key, sortQuestSeriesSection(quests)] as [string, QuestSeries[]]
  )

  const routineSectionsWithOneSeriesPerGroup = sortedRoutineSections.map(
    ([key, quests]) => {
      const seenQuestGroupIds = new Set()
      const filteredQuests = quests.filter((quest) => {
        if (quest.quest_group_id === null) {
          return true
        }
        if (!seenQuestGroupIds.has(quest.quest_group_id)) {
          seenQuestGroupIds.add(quest.quest_group_id)
          return true
        }
        return false
      })
      return [key, filteredQuests] as [string, QuestSeries[]]
    }
  )

  return {
    routineSections: routineSectionsWithOneSeriesPerGroup,
  }
}

const sortQuestSections = (questSections: Record<string, QuestSeries[]>) => {
  const order: Record<string, number> = {
    morning: 0,
    afternoon: 1,
    night: 2,
    anytime: 3,
  }
  return Object.entries(questSections).sort(
    ([keyA], [keyB]) => (order[keyA] ?? Infinity) - (order[keyB] ?? Infinity)
  )
}

const isEqual = (
  a: [string, QuestSeries[]][],
  b: [string, QuestSeries[]][]
) => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const [keyA, questsA] = a[i]
    const [keyB, questsB] = b[i]
    if (keyA !== keyB || questsA.length !== questsB.length) return false
    for (let j = 0; j < questsA.length; j++) {
      if (questsA[j].id !== questsB[j].id) return false
    }
  }
  return true
}
